import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Spin } from 'antd';
import ConsumerMap from '../../../components/map/ConsumerMap';
import { analysisService } from '../../../api/services/analysisService';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { getColorByIntensity, normalizeString } from '../../../utils/defaultMapFunctions';
import {
  CityLocationFeature,
  DistrictLocationFeature,
  CityLocationProperties,
  DistrictLocationProperties,
} from '../../../components/map/iConsumerMap';

interface AlertData {
  id: number;
  productName: string;
  lastReportedPrice: number;
  triggeredAt: number;
  currency: string;
}

interface AlertMapData {
  [locationKey: string]: {
    alertCount: number;
    alerts: AlertData[];
    maxPrice: number;
    minPrice: number;
    avgPrice: number | null;
    currency: string;
    locationId: number;
    locationName: string;
    district: string;
    city: string;
  };
}

interface AlertMapProps {
  t: (key: string) => string;
}

const AlertMap: React.FC<AlertMapProps> = ({ t }) => {
  const [presentations, setPresentations] = useState<{ refId: string; name: string }[]>([]);
  const [alertMapData, setAlertMapData] = useState<AlertMapData>({});
  const [selectedPresentation, setSelectedPresentation] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('7d');
  const [loading, setLoading] = useState(false);
  const alertMapDataRef = useRef(alertMapData);

  const timeframes = [
    { label: t('last24hours'), value: '1d' },
    { label: t('last7Days'), value: '7d' },
    { label: t('last30Days'), value: '30d' },
    { label: t('last90Days'), value: '90d' },
  ];

  const fetchPresentations = useCallback(async () => {
    try {
      const presentationsData = await referenceDataSyncService.getPresentations();
      setPresentations(presentationsData);
    } catch (error) {
      console.error('Error fetching presentations:', error);
    }
  }, []);

  const fetchAlertMapData = useCallback(async () => {
    try {
      setLoading(true);
      const query: { timeframe: string; productRefId?: string } = {
        timeframe: selectedTimeframe,
      };

      if (selectedPresentation) {
        query.productRefId = selectedPresentation;
      }

      const response = await analysisService.getAlertMapData(query);
      setAlertMapData(response?.mapData as AlertMapData);
    } catch (error) {
      console.error('Error fetching alert map data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPresentation, selectedTimeframe, t]);

  useEffect(() => {
    fetchPresentations();
  }, [fetchPresentations]);

  useEffect(() => {
    fetchAlertMapData();
  }, [fetchAlertMapData]);

  const getMaxAlertCount = useCallback(() => {
    const counts = Object.values(alertMapData).map((data) => data.alertCount);
    return Math.max(...counts, 1);
  }, [alertMapData]);

  const districtStyleFunction = useCallback(
    (feature: DistrictLocationFeature | undefined) => {
      const districtName = feature?.properties.district;
      const locationData = Object.values(alertMapData).find((data) => {
        const alertDistrict = data.district?.replace(/\s+/g, '');
        const mapDistrict = districtName?.replace(/\s+/g, '');
        return alertDistrict === mapDistrict;
      });

      if (!locationData || locationData.alertCount === 0) {
        return {
          fillColor: '#e5e7eb',
          fillOpacity: 0.3,
          color: '#d1d5db',
          weight: 1,
        };
      }

      const maxAlertCount = getMaxAlertCount();
      return {
        fillColor: getColorByIntensity(locationData.alertCount, maxAlertCount),
        fillOpacity: 0.6,
        color: '#fff',
        weight: 1,
      };
    },
    [alertMapData, getMaxAlertCount]
  );

  const cityStyleFunction = useCallback(
    (feature: CityLocationFeature | undefined) => {
      const locationId = feature?.properties.locationId;
      const district = feature?.properties.district;

      let locationData = null;

      if (locationId) {
        locationData = alertMapData[locationId.toString()];
      }

      if (!locationData && district) {
        locationData = Object.values(alertMapData).find(
          (data) => normalizeString(data.district) === district
        );
      }

      if (!locationData || locationData.alertCount === 0) {
        return {
          fillColor: '#e5e7eb',
          fillOpacity: 0.2,
          color: '#d1d5db',
          weight: 1,
        };
      }

      const maxAlertCount = getMaxAlertCount();
      const hasSpecificLocationAlerts = Object.values(alertMapData).some(
        (data) => data.locationId && data.locationId > 0
      );

      return {
        fillColor: getColorByIntensity(locationData.alertCount, maxAlertCount),
        fillOpacity: hasSpecificLocationAlerts ? 0.8 : 0.6,
        color: '#fff',
        weight: hasSpecificLocationAlerts ? 2 : 1,
      };
    },
    [alertMapData, getMaxAlertCount]
  );

  const districtBorderStyleFunction = useCallback(() => {
    return {
      color: '#6b7280',
      weight: 1,
      opacity: 0.5,
    };
  }, []);

  const popupContentFunction = useCallback(
    (feature: CityLocationFeature | DistrictLocationFeature) => {
      let locationData;
      let locationName;

      if (feature?.properties && 'locationId' in feature.properties) {
        const cityProps = feature.properties as CityLocationProperties;
        locationData = alertMapDataRef.current[cityProps.locationId.toString()];
        locationName = locationData?.locationName || cityProps.city || t('unknownLocation');
      } else if (feature?.properties?.district) {
        const districtProps = feature.properties as DistrictLocationProperties;
        locationData = Object.values(alertMapDataRef.current).find((data) => {
          const alertDistrict = data.district?.replace(/\s+/g, '');
          const mapDistrict = districtProps.district?.replace(/\s+/g, '');
          return alertDistrict === mapDistrict;
        });
        locationName = districtProps.district;
      } else {
        locationName = t('unknownLocation');
      }

      if (!locationData || locationData?.alertCount === 0) {
        return `
        <div class="p-2">
          <h3 class="font-semibold text-gray-800">${locationName}</h3>
          <p class="text-gray-600">${t('noAlertsInSelectedTimeframe')}</p>
        </div>
      `;
      }

      return `
      <div class="p-3 max-w-xs">
        <h3 class="font-semibold text-gray-800 mb-2">${locationName}</h3>
        <div class="mb-2">
          <span class="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
            ${locationData?.alertCount} ${t('alerts')}
          </span>
        </div>
      </div>
    `;
    },
    [alertMapData, t]
  );

  useEffect(() => {
    alertMapDataRef.current = alertMapData;
  }, [alertMapData]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">{t('presentations')}:</label>
          <Select
            className="w-[200px] [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('makeASelection')}
            allowClear
            value={selectedPresentation}
            onChange={setSelectedPresentation}
            options={presentations.map((presentation) => ({
              label: presentation?.name,
              value: presentation?.refId,
            }))}
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">{t('timeframe')}:</label>
          <Select
            className="w-[150px] [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            value={selectedTimeframe}
            onChange={setSelectedTimeframe}
            options={timeframes}
          />
        </div>
      </div>

      <div className="relative">
        <div className="flex min-h-[400px] items-center justify-center text-6xl text-primary-gray-300">
          <ConsumerMap
            districtStyleFunction={districtStyleFunction}
            cityStyleFunction={cityStyleFunction}
            districtBorderStyleFunction={districtBorderStyleFunction}
            popupContentFunction={popupContentFunction}
            triggeredFrom={'alertMap'}
          />
        </div>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <Spin size="large" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-gray-200"></div>
          <span>{t('noAlerts')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="h-4 w-4 rounded"
            style={{ backgroundColor: 'rgba(0, 76, 211, 0.2)' }}
          ></div>
          <span>{t('lowAlerts')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="h-4 w-4 rounded"
            style={{ backgroundColor: 'rgba(0, 76, 211, 0.6)' }}
          ></div>
          <span>{t('mediumAlerts')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(0, 76, 211, 1)' }}></div>
          <span>{t('highAlerts')}</span>
        </div>
      </div>
    </div>
  );
};

export default AlertMap;
